import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Settings2, AlertCircle, Dices, Users, UserCheck } from 'lucide-react';
// import { cn } from '@/lib/utils';
import type { UseGameStateReturn } from '@/hooks/use-game-state';
// import { LanguageSelector } from './LanguageSelector';
import { TeamBoard } from './game/TeamBoard';
import { SubmissionQueue } from './game/SubmissionQueue';
import { defaultTriggers, defaultCategories } from '../config/defaultLists';
import { ConfigImport } from './ConfigImport';
import { ResponseExporter } from './ResponseExporter';
import { ResponseStorageService } from '../services/response-storage';
import { teams } from '../config/teams';

// interface Team {
//  id: string;
//  name: string;
//  progress?: number;
//  level?: number;
// }

// interface Submission {
//  teamId: string;
//  content: string[];
//  validated?: boolean;
// }

interface RollingState {
  [key: string]: boolean;
}

interface MasterViewProps extends Omit<UseGameStateReturn, 'status'> {
  status: "connecting" | "connected" | "disconnected" | "error";
  customTriggers?: string[];
  customCategories?: string[];
  language?: 'fr' | 'en' | 'ar';
}

type TeamColorScheme = {
  primary: string;
  secondary: string;
  light: string;
  text: string;
  hover: string;
  border: string;
};

type TeamColors = {
  [key: number]: TeamColorScheme;
};

type SupportedLanguage = 'fr' | 'en' | 'ar';

const teamColors: TeamColors = {
  0: {
    primary: 'bg-blue-600',
    secondary: 'bg-blue-400',
    light: 'bg-blue-100',
    text: 'text-blue-600',
    hover: 'hover:bg-blue-700',
    border: 'border-blue-200'
  },
  1: {
    primary: 'bg-emerald-600',
    secondary: 'bg-emerald-400',
    light: 'bg-emerald-100',
    text: 'text-emerald-600',
    hover: 'hover:bg-emerald-700',
    border: 'border-emerald-200'
  },
  2: {
    primary: 'bg-purple-600',
    secondary: 'bg-purple-400',
    light: 'bg-purple-100',
    text: 'text-purple-600',
    hover: 'hover:bg-purple-700',
    border: 'border-purple-200'
  }
};

function ErrorState() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
      <Card className="p-8 text-center max-w-md">
        <div className="mb-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
        </div>
        <h2 className="text-2xl font-bold text-red-600 mb-4">{t('game.error.title')}</h2>
        <p className="text-muted-foreground mb-6">
          {t('game.error.description')}
        </p>
        <Button
          onClick={() => {
            const basePath = import.meta.env.PROD ? '/storm' : '';
            window.location.href = `${window.location.origin}${basePath}${window.location.pathname}${window.location.search}`;
          }}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          {t('game.error.retry')}
        </Button>
      </Card>
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
      <Card className="p-8 text-center">
        <Dices className="h-12 w-12 mx-auto mb-4 text-purple-600 animate-spin" />
        <h2 className="text-xl font-bold mb-2">{message}</h2>
      </Card>
    </div>
  );
}

export function MasterView({ 
  state, 
  actions, 
  status, 
  customTriggers, 
  customCategories,
  language = 'fr' 
}: MasterViewProps) {
  const { t, i18n } = useTranslation();
  const [isRolling, setIsRolling] = useState<RollingState>({});
  const [teamDice, setTeamDice] = useState<Record<string, { trigger: string, category: string }>>({});
  
  const currentLanguage = (i18n.language || language) as SupportedLanguage;
  
  const [triggers, setTriggers] = useState<string[]>(
    customTriggers || defaultTriggers[currentLanguage]
  );
  const [categories, setCategories] = useState<string[]>(
    customCategories || defaultCategories[currentLanguage]
  );
  
  const [selectedTriggers, setSelectedTriggers] = useState<Set<string>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [newTrigger, setNewTrigger] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [importedConfig, setImportedConfig] = useState<any>(null);
  // Get feminine mode from game state or default to false
  const isFeminineMode = state?.gameBoard?.isFeminineMode || false;

  // Update lists when language changes
  useEffect(() => {
    const currentLang = (i18n.language || language) as SupportedLanguage;
    if (importedConfig) {
      setTriggers(importedConfig.triggers[currentLang] || []);
      setCategories(importedConfig.categories[currentLang] || []);
    } else {
      setTriggers(customTriggers || defaultTriggers[currentLang]);
      setCategories(customCategories || defaultCategories[currentLang]);
    }
  }, [i18n.language, language, customTriggers, customCategories, importedConfig]);

  // Handle config import
  const handleConfigUpdate = (config: any) => {
    setImportedConfig(config);
    const currentLang = (i18n.language || language) as SupportedLanguage;
    setTriggers(config.triggers[currentLang] || []);
    setCategories(config.categories[currentLang] || []);
  };

  // Get team name based on feminine mode
  const getTeamName = (team: any) => {
    const nameKey = isFeminineMode ? team.nameFeminine : team.name;
    return t(nameKey);
  };

  // Toggle feminine mode and sync with server
  const toggleFeminineMode = () => {
    const newMode = !isFeminineMode;
    // Send to server to update all clients
    actions.updateFeminineMode(newMode);
  };

  // Update team dice when game state changes
  useEffect(() => {
    if (state?.gameBoard?.teamChallenges) {
      const newTeamDice: Record<string, { trigger: string, category: string }> = {};
      Object.entries(state.gameBoard.teamChallenges).forEach(([teamId, challenge]) => {
        newTeamDice[teamId] = {
          trigger: challenge.trigger || "?",
          category: challenge.category || "?"
        };
      });
      setTeamDice(newTeamDice);
    }
  }, [state?.gameBoard?.teamChallenges]);

  // Handle different states
  if (status === "error") {
    return <ErrorState />;
  }

  if (!state || !actions) {
    return <LoadingState message={t('game.messages.connecting')} />;
  }

  if (status === "connecting") {
    return <LoadingState message={t('game.messages.initializing')} />;
  }

  const handleRollDice = async (teamId: string) => {
    setIsRolling(prev => ({ ...prev, [teamId]: true }));
    
    setTeamDice(prev => ({
      ...prev,
      [teamId]: {
        trigger: "🎲",
        category: "🎲"
      }
    }));

    // Add a delay to simulate rolling
    await new Promise(resolve => setTimeout(resolve, 1000));

    actions.rollDice(teamId, i18n.language || language);

    // Keep the rolling state for a bit longer to ensure the animation is visible
    setTimeout(() => {
      setIsRolling(prev => ({ ...prev, [teamId]: false }));
    }, 2000);
  };

  const handleAddTrigger = () => {
    if (newTrigger.trim()) {
      const newTriggers = newTrigger.split(",").map(t => t.trim()).filter(Boolean);
      setTriggers(prev => [...prev, ...newTriggers]);
      setNewTrigger("");
    }
  };

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      const newCategories = newCategory.split(",").map(c => c.trim()).filter(Boolean);
      setCategories(prev => [...prev, ...newCategories]);
      setNewCategory("");
    }
  };

  const handleDeleteTriggers = () => {
    const selectedSet = selectedTriggers;
    setTriggers(prev => prev.filter(trigger => !selectedSet.has(trigger)));
    setSelectedTriggers(new Set());
  };

  const handleDeleteCategories = () => {
    const selectedSet = selectedCategories;
    setCategories(prev => prev.filter(category => !selectedSet.has(category)));
    setSelectedCategories(new Set());
  };

  const handleResetToDefaults = () => {
    setTriggers(defaultTriggers[language]);
    setCategories(defaultCategories[language]);
    setSelectedTriggers(new Set());
    setSelectedCategories(new Set());
  };

  // Enhanced validation handler that saves responses
  const handleValidateSubmission = async (teamId: string, isValid: boolean) => {
    // Find the submission to get the content
    const submission = state?.gameBoard?.submissionQueue?.find(s => s.teamId === teamId);
    
    if (submission && state?.sessionId) {
      // Get team name
      const teamConfig = teams.find(t => t.id === teamId);
      const teamName = teamConfig ? teamConfig.name : `Team ${teamId}`;
      
      // Get current challenge for this team
      const teamChallenge = state?.gameBoard?.teamChallenges?.[teamId];
      
      // Save each idea as a separate response
      for (const idea of submission.content) {
        await ResponseStorageService.saveResponse(
          state.sessionId,
          teamId,
          teamName,
          teamChallenge || {},
          idea,
          isValid
        );
      }
    }
    
    // Call the original validation handler
    actions.validateSubmission(teamId, isValid);
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto">
        <Tabs defaultValue="game" className="w-full">
        <TabsList className="flex w-full max-w-[800px] mx-auto gap-2 p-1 bg-white rounded-full mb-8">
          <TabsTrigger 
            value="game" 
            className="flex-1 rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-colors"
          >
            <Dices className="mr-2 h-4 w-4" />
            {t('game.tabs.game')}
          </TabsTrigger>
          <TabsTrigger 
            value="settings" 
            className="flex-1 rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-colors"
          >
            <Settings2 className="mr-2 h-4 w-4" />
            {t('game.tabs.settings')}
          </TabsTrigger>
          <TabsTrigger 
            value="import" 
            className="flex-1 rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-colors"
          >
            📁 Import
          </TabsTrigger>
          <TabsTrigger 
            value="export" 
            className="flex-1 rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-colors"
          >
            📊 Export
          </TabsTrigger>
        </TabsList>

          <TabsContent value="game">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Section - Game Board */}
              <div className="w-full lg:w-2/3">
                <TeamBoard
                  teamStates={state.teams}
                  submissionQueue={state.gameBoard.submissionQueue}
                  teamColors={teamColors}
                  onRollDice={handleRollDice}
                  isRolling={isRolling}
                  status={status}
                  teamDice={teamDice}
                  isFeminineMode={isFeminineMode}
                  getTeamName={getTeamName}
                />
              </div>

              {/* Right Section - Submission Queue */}
              <div className="w-full lg:w-1/3">
                <Card className="p-4 lg:p-6 lg:sticky lg:top-6">
                  <h3 className="text-lg lg:text-xl font-semibold mb-4">
                    <span className="inline-flex items-center gap-2">
                      📥 {t('game.queue.title')}
                      {state.gameBoard.submissionQueue.length > 0 && (
                        <span className="bg-purple-100 text-purple-600 text-sm px-2 py-1 rounded-full">
                          {state.gameBoard.submissionQueue.length}
                        </span>
                      )}
                    </span>
                  </h3>
                <SubmissionQueue
                  submissions={state.gameBoard.submissionQueue}
                  onValidate={handleValidateSubmission}
                />
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <div className="max-w-3xl mx-auto space-y-8">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {t('game.settings.teams.title')}
                  </h2>
                      <Button
                        onClick={toggleFeminineMode}
                        className={`flex items-center gap-2 ${
                          isFeminineMode
                            ? 'bg-pink-600 hover:bg-pink-700 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                    {isFeminineMode ? (
                      <>
                        <UserCheck className="h-4 w-4" />
                        {t('game.settings.teams.feminineMode')}
                      </>
                    ) : (
                      <>
                        <Users className="h-4 w-4" />
                        {t('game.settings.teams.masculineMode')}
                      </>
                    )}
                  </Button>
                </div>
                <Button
                  onClick={handleResetToDefaults}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-600"
                >
                  {t('game.actions.resetToDefaults')}
                </Button>
              </div>
              
              {/* Team Names Preview */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {t('game.settings.teams.toggleDescription')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {teams.map((team) => (
                    <div key={team.id} className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600 mb-2">Équipe {team.id.slice(-1)}</div>
                      <div className="font-semibold text-lg">
                        {getTeamName(team)}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
              
              {/* Triggers Section */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">
                  {t('game.settings.triggers.add')}
                </h3>
                <div className="flex gap-3">
                  <Input
                    value={newTrigger}
                    onChange={(e) => setNewTrigger(e.target.value)}
                    placeholder={t('game.settings.triggers.placeholder')}
                    className="flex-1 bg-white/50 backdrop-blur-sm text-base"
                  />
                  <Button
                    onClick={handleAddTrigger}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                  >
                    {t('game.actions.add')}
                  </Button>
                </div>
              </Card>

              {/* Manage Triggers Section */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">
                  {t('game.settings.triggers.manage')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                  {triggers.map((trigger) => (
                    <div key={trigger} className="flex items-center gap-2">
                      <Checkbox
                        id={trigger}
                        checked={selectedTriggers.has(trigger)}
                        onCheckedChange={(checked: boolean) => {
                          setSelectedTriggers(prev => {
                            const newSet = new Set(prev);
                            if (checked) {
                              newSet.add(trigger);
                            } else {
                              newSet.delete(trigger);
                            }
                            return newSet;
                          });
                        }}
                        className="border-purple-300 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                      />
                      <label
                        htmlFor={trigger}
                        className="text-xs font-medium leading-none cursor-pointer select-none"
                      >
                        {trigger}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedTriggers.size > 0 && (
                  <Button
                    onClick={handleDeleteTriggers}
                    className="mt-6 bg-red-100 hover:bg-red-200 text-red-600 border border-red-200"
                  >
                    {t('game.actions.deleteSelected')}
                  </Button>
                )}
              </Card>

              {/* Categories Section */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">
                  {t('game.settings.categories.add')}
                </h3>
                <div className="flex gap-3">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder={t('game.settings.categories.placeholder')}
                    className="flex-1 bg-white/50 backdrop-blur-sm text-base"
                  />
                  <Button
                    onClick={handleAddCategory}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                  >
                    {t('game.actions.add')}
                  </Button>
                </div>
              </Card>

              {/* Manage Categories Section */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">
                  {t('game.settings.categories.manage')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                  {categories.map((category) => (
                    <div key={category} className="flex items-center gap-2">
                      <Checkbox
                        id={category}
                        checked={selectedCategories.has(category)}
                        onCheckedChange={(checked: boolean) => {
                          setSelectedCategories(prev => {
                            const newSet = new Set(prev);
                            if (checked) {
                              newSet.add(category);
                            } else {
                              newSet.delete(category);
                            }
                            return newSet;
                          });
                        }}
                        className="border-purple-300 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                      />
                      <label
                        htmlFor={category}
                        className="text-xs font-medium leading-none cursor-pointer select-none"
                      >
                        {category}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedCategories.size > 0 && (
                  <Button
                    onClick={handleDeleteCategories}
                    className="mt-6 bg-red-100 hover:bg-red-200 text-red-600 border border-red-200"
                  >
                    {t('game.actions.deleteSelected')}
                  </Button>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="import">
            <ConfigImport 
              sessionId={state.sessionId || 'default'} 
              onConfigUpdate={handleConfigUpdate}
            />
          </TabsContent>

          <TabsContent value="export">
            <ResponseExporter 
              sessionId={state.sessionId || 'default'} 
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
